/** Delete with `@digibee/beehive-ui`: with no sheet open this returns null and HeroUI's default takes over. */

const RADIX_DIALOG = '[role="dialog"][data-state="open"]';
const POPPER_WRAPPER = '[data-radix-popper-content-wrapper]';

export const RADIX_OVERLAY_HOST = 'data-radix-overlay-host';

const HOST_SELECTOR = `dialog[${RADIX_OVERLAY_HOST}]`;
const STYLE_ID = 'radix-overlay-host-style';

const findOpenSheet = (): HTMLElement | null => {
  if (typeof document === 'undefined') return null;

  const nodes = document.querySelectorAll<HTMLElement>(RADIX_DIALOG);

  for (let i = nodes.length - 1; i >= 0; i -= 1) {
    const node = nodes[i];
    // Radix Popover shares the dialog role; portaling there would unmount with it.
    if (node && !node.closest(POPPER_WRAPPER)) return node;
  }

  return null;
};

/** Neutralises the UA styling of `<dialog>`; only `pointer-events` is ours. */
const HOST_CSS = `
${HOST_SELECTOR} {
  position: fixed;
  inset: 0;
  width: auto;
  height: auto;
  max-width: none;
  max-height: none;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  overflow: visible;
  pointer-events: auto;
}
${HOST_SELECTOR}::backdrop {
  background: transparent;
}
`;

const ensureStyles = () => {
  if (document.getElementById(STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = HOST_CSS;
  document.head.appendChild(style);
};

/** A native dialog closes itself on Escape; dismissal belongs to the overlay. */
const preventNativeDismiss = (event: Event) => event.preventDefault();

/** Portal target for a HeroUI overlay over a Beehive SideSheet; null when the default works. */
export const resolveRadixOverlayHost = (): HTMLElement | null => {
  const sheet = findOpenSheet();
  if (!sheet) return null;

  ensureStyles();

  const existing = sheet.querySelector<HTMLDialogElement>(HOST_SELECTOR);
  const host = existing ?? document.createElement('dialog');

  if (!existing) {
    host.setAttribute(RADIX_OVERLAY_HOST, '');
    host.addEventListener('cancel', preventNativeDismiss);
    // Radix uses `contains` to detect outside clicks: from <body> a click here would
    // dismiss the sheet behind it.
    sheet.appendChild(host);
  }

  // Top layer: the sheet's transform stops positioning it (the off-centre bug) and the
  // rest of the document goes inert, so the Radix focus trap cannot steal focus back.
  if (!host.open) host.showModal?.();

  return host;
};

/** An open dialog left in the top layer makes the whole page unclickable. */
export const releaseRadixOverlayHost = (
  host: HTMLElement | null | undefined
) => {
  if (!host) return;

  const dialog = host as HTMLDialogElement;
  dialog.removeEventListener('cancel', preventNativeDismiss);
  if (dialog.open) dialog.close?.();
  dialog.remove();
};
